import * as React from "react";
import {
  OpportunityEntry,
  Reseller as ApiReseller,
  useGetV4ManufacturerByRouteManufacturerIdOpportunitiesQuery,
  usePutV4ManufacturerByRouteManufacturerIdOpportunityAndBidNumberResellerResellerIdMutation,
} from "randmar-api-client";
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useToast,
} from "randmarcomps";
import { useManufacturer } from "../../contexts/WithManufacturer";
import LegacyReseller from "../../stores/reseller/Reseller";
import { getUserSelectedOpportunity, setUserSelectedOpportunity } from "../../utils/LocalStorageUtils";
import CenteredLoader from "../util/CenteredLoader";

interface IProps {
  reseller: LegacyReseller | ApiReseller;
  children: React.ReactNode;
}

const isLegacyReseller = (reseller: LegacyReseller | ApiReseller): reseller is LegacyReseller =>
  "resellerId" in reseller && !("ResellerId" in reseller);

const AddResellerToOpportunityWrapper: React.FC<IProps> = ({ reseller: rawReseller, children }) => {
  const { applicationId } = useManufacturer();
  const [open, setOpen] = React.useState(false);
  const [chosenOpportunity, setChosenOpportunity] = React.useState(getUserSelectedOpportunity() || "");
  const { toast } = useToast();

  const { data: opportunitiesData, isLoading: isLoadingOpportunities } = useGetV4ManufacturerByRouteManufacturerIdOpportunitiesQuery(
    { routeManufacturerId: applicationId },
    { skip: !applicationId }
  );
  const opportunities = Array.isArray(opportunitiesData) ? (opportunitiesData as OpportunityEntry[]) : [];
  const [addReseller, { isLoading: isAdding }] = usePutV4ManufacturerByRouteManufacturerIdOpportunityAndBidNumberResellerResellerIdMutation();

  const reseller = React.useMemo<ApiReseller>(() => {
    if (isLegacyReseller(rawReseller)) {
      return {
        ResellerId: rawReseller.resellerId ? String(rawReseller.resellerId) : undefined,
        Name: rawReseller.name,
      };
    }
    return rawReseller;
  }, [rawReseller]);

  const handleOpenChange = React.useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        setOpen(false);
        return;
      }

      const stored = getUserSelectedOpportunity() || "";
      const defaultBidNumber = stored || opportunities[0]?.BidNumber || "";
      setChosenOpportunity(defaultBidNumber);
      setOpen(true);
    },
    [opportunities]
  );

  const handleConfirmResellerOpportunityRequest = React.useCallback(async () => {
    if (!applicationId) return;
    if (!reseller.ResellerId) return;
    if (!chosenOpportunity) return;

    try {
      await addReseller({
        routeManufacturerId: applicationId,
        bidNumber: chosenOpportunity,
        resellerId: reseller.ResellerId,
      }).unwrap();
      toast({ variant: "success", title: "Success", description: `${reseller.Name || "Reseller"} added to opportunity ${chosenOpportunity}.` });
      setOpen(false);
    } catch {
      toast({ variant: "destructive", title: "Error", description: "Could not add reseller to the selected opportunity." });
    }
  }, [addReseller, applicationId, chosenOpportunity, reseller.Name, reseller.ResellerId, toast]);

  const isLoading = isLoadingOpportunities || isAdding;
  const hasOpportunities = opportunities.length > 0;

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(nextOpen) => {
          if (isLoading) return;
          handleOpenChange(nextOpen);
        }}
      >
        <DialogTrigger asChild>{children}</DialogTrigger>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[20px] font-medium text-slate-900">Add reseller to an opportunity</DialogTitle>
          </DialogHeader>
          {isLoading ? (
            <CenteredLoader variant="plain" className="min-h-[200px]" />
          ) : (
            <>
              <div className="py-4">
                <p className="text-[15px] text-slate-600">
                  Select the opportunity you want to link {reseller.Name || "this reseller"} to.
                </p>
                <Select
                  value={chosenOpportunity}
                  onValueChange={(newValue) => {
                    setChosenOpportunity(newValue);
                    setUserSelectedOpportunity(newValue || "");
                  }}
                >
                  <SelectTrigger className="mt-3 h-11 rounded-lg">
                    <SelectValue placeholder="Select an opportunity" />
                  </SelectTrigger>
                  <SelectContent>
                    {opportunities.map((opportunity) => (
                      <SelectItem key={`Opportunity-${opportunity.BidNumber || "unknown"}`} value={opportunity.BidNumber || ""}>
                        <span className="font-semibold">{opportunity.BidNumber}</span>
                        {opportunity.Opportunity ? <span className="text-slate-600"> — {opportunity.Opportunity}</span> : null}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
                <Button type="button" variant="outline" className="rounded-xl" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={handleConfirmResellerOpportunityRequest}
                  className="rounded-xl"
                  disabled={!hasOpportunities || chosenOpportunity === ""}
                >
                  Confirm
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AddResellerToOpportunityWrapper;
